import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ShieldAlert, KeyRound, Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { EnhancedHeader } from "@/components/ui";

// Define schema for password change
const passwordChangeSchema = z
  .object({
    currentPassword: z
      .string()
      .min(6, "Current password must be at least 6 characters"),
    newPassword: z
      .string()
      .min(6, "New password must be at least 6 characters"),
    confirmPassword: z
      .string()
      .min(6, "Confirm password must be at least 6 characters"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type PasswordChangeValues = z.infer<typeof passwordChangeSchema>;

export default function PasswordChangePage() {
  const { toast } = useToast();

  // Password visibility states
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Create form
  const form = useForm<PasswordChangeValues>({
    resolver: zodResolver(passwordChangeSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  // Password change mutation
  const passwordChangeMutation = useMutation({
    mutationFn: async (data: {
      currentPassword: string;
      newPassword: string;
    }) => {
      return apiRequest("POST", "/api/auth/change-password", data);
    },
    onSuccess: () => {
      toast({
        title: "Password Changed",
        description: "Your password has been successfully updated.",
      });
      form.reset();
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to Change Password",
        description:
          error.message ||
          "There was a problem changing your password. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Submit handler
  async function onSubmit(data: PasswordChangeValues) {
    passwordChangeMutation.mutate({
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
    });
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Enhanced Header */}
      <EnhancedHeader
        title="Change Password"
        backPath="/user-settings"
        coloredText={true}
        rightContent={null}
        showBackButton={true}
      />

      <div className="max-w-md mx-auto px-4 pb-8">
        {/* Security Notice */}
        <div className="mb-5 p-4 rounded-lg bg-green-950/30 border border-green-500/30">
          <div className="flex items-start gap-3">
            <ShieldAlert className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium mb-1">Security Notice</h3>
              <p className="text-sm text-gray-300">
                Use a strong password that you don't use for other services. For
                better security, include a mix of letters, numbers, and symbols.
              </p>
            </div>
          </div>
        </div>

        {/* Password Form */}
        <div className="bg-zinc-900 rounded-xl overflow-hidden">
          <h3 className="text-xs uppercase text-gray-400 font-medium px-4 pt-3 pb-2">
            Password Details
          </h3>

          <div className="p-4">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="currentPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-300">Current Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            placeholder="Enter your current password"
                            type={showCurrentPassword ? "text" : "password"}
                            {...field}
                            disabled={passwordChangeMutation.isPending}
                            className="pr-10 bg-zinc-800 border-zinc-700 focus-visible:ring-green-500 text-white"
                          />
                          <button
                            type="button"
                            className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-300"
                            onClick={() =>
                              setShowCurrentPassword(!showCurrentPassword)
                            }
                          >
                            {showCurrentPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage className="text-red-400" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="newPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-300">New Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            placeholder="Enter new password"
                            type={showNewPassword ? "text" : "password"}
                            {...field}
                            disabled={passwordChangeMutation.isPending}
                            className="pr-10 bg-zinc-800 border-zinc-700 focus-visible:ring-green-500 text-white"
                          />
                          <button
                            type="button"
                            className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-300"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                          >
                            {showNewPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage className="text-red-400" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-300">Confirm New Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            placeholder="Confirm new password"
                            type={showConfirmPassword ? "text" : "password"}
                            {...field}
                            disabled={passwordChangeMutation.isPending}
                            className="pr-10 bg-zinc-800 border-zinc-700 focus-visible:ring-green-500 text-white"
                          />
                          <button
                            type="button"
                            className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-300"
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                          >
                            {showConfirmPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage className="text-red-400" />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full bg-green-600 hover:bg-green-700 mt-2"
                  disabled={passwordChangeMutation.isPending}
                >
                  {passwordChangeMutation.isPending
                    ? "Changing Password..."
                    : "Change Password"}
                </Button>
              </form>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
}
